import React, { useState, useEffect, useRef } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Deal, Product, Client, Company, Quote, catalogueAPI, clientsAPI, companiesAPI, quotesAPI, stockAlertsAPI, CreateQuoteDto, CreateQuoteItemDto, InventoryAvailabilityStatus } from '../../services/api';
import { Modal, Button, Card, CardBody } from '../ui';

interface QuoteModalProps {
  quote?: Quote;
  deal?: Deal;
  client?: {
    id: number;
    name: string;
    email?: string;
    phoneNumbers?: string[];
  };
  onClose: () => void;
  onSuccess?: () => void;
  prefillData?: {
    productId?: number;
    productName?: string;
    productSku?: string;
    quantity?: number;
    unit?: string;
  };
}

const formatDateForInput = (value?: string | null) => {
  if (!value) return '';
  return value.split('T')[0];
};

const normalizeUnit = (value?: string | null) => value?.trim().toLowerCase() || '';

const getStockKey = (productId?: number, unit?: string | null) => {
  if (!productId) return '';
  return `${productId}:${normalizeUnit(unit)}`;
};

const CLIENT_SEARCH_PAGE_SIZE = 20;

interface ClientPaginationState {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const mergeClientResults = (existing: Client[], incoming: Client[]) => {
  const clientsById = new Map<number, Client>();
  existing.forEach((client) => clientsById.set(client.id, client));
  incoming.forEach((client) => clientsById.set(client.id, client));
  return Array.from(clientsById.values());
};

const QuoteModal: React.FC<QuoteModalProps> = ({ quote, deal, client, onClose, onSuccess, prefillData }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(quote?.companyId ? String(quote.companyId) : '');
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | undefined>();
  const [clientSearch, setClientSearch] = useState('');
  const [clientPagination, setClientPagination] = useState<ClientPaginationState>({
    page: 1,
    limit: CLIENT_SEARCH_PAGE_SIZE,
    total: 0,
    pages: 1,
  });
  const [loadingClients, setLoadingClients] = useState(false);
  const clientRequestId = useRef(0);
  const [stockDetails, setStockDetails] = useState<Record<string, {
    productId: number;
    productName: string;
    sku: string;
    isInventoryLinked: boolean;
    requiredQuantity: number;
    availableQuantity: number;
    status: InventoryAvailabilityStatus;
    requestedUnit?: string | null;
    inventoryUnit?: string | null;
    unitMismatch?: boolean;
    inventoryItems: Array<{
      id: number;
      quantity: number;
      batchNumber: string | null;
      warehouseName?: string;
      location: string;
    }>;
  }>>({});
  const [checkingStock, setCheckingStock] = useState(false);

  const shouldSelectClient = !quote && !deal && !client;
  const quoteClient = client || deal?.client || quote?.client || selectedClient;
  const hasMoreClients = clientPagination.page < clientPagination.pages;

  // Auto-fill title and introduction from deal
  const defaultTitle = quote?.title
    || (deal?.description
      ? `Quote for ${deal.description}`
      : `Quote for ${quoteClient?.name || 'Client'}`);
  const defaultIntroduction = quote?.introduction || (quoteClient?.name
    ? `Dear ${quoteClient.name},\n\nThank you for your interest. Please find our quote below${deal?.description ? ` for ${deal.description}` : ''}.`
    : `Thank you for your interest. Please find our quote below.`);

  const [formData, setFormData] = useState({
    title: defaultTitle,
    introduction: defaultIntroduction,
    terms: quote?.terms || 'Payment terms: Net 30 days\nDelivery: 2-3 weeks from order confirmation\nPrices are valid for 30 days',
    notes: quote?.notes || '',
    currency: quote?.currency || deal?.currency || 'USD',
    discountPercent: Number(quote?.discountPercent || 0),
    taxPercent: Number(quote?.taxPercent ?? 10),
    validUntil: formatDateForInput(quote?.validUntil || quote?.expiresAt),
  });

  // Pre-fill items from deal.orderItems or prefillData
  const getInitialItems = (): CreateQuoteItemDto[] => {
    if (quote?.items && quote.items.length > 0) {
      return quote.items.map((item) => ({
        productId: item.productId,
        name: item.name,
        description: item.description,
        sku: item.sku || '',
        quantity: Number(item.quantity),
        unit: item.unit || 'pcs',
        unitPrice: Number(item.unitPrice),
        discountAmount: Number(item.discountAmount || 0),
        discountPercent: Number(item.discountPercent || 0),
        isOptional: item.isOptional,
        order: item.order,
      }));
    }

    // If prefillData is provided (from sample), use it
    if (prefillData?.productName) {
      return [{
        productId: prefillData.productId,
        name: prefillData.productName,
        sku: prefillData.productSku || '',
        quantity: prefillData.quantity || 1,
        unit: prefillData.unit || 'pcs',
        unitPrice: 0,
        discountPercent: 0,
      }];
    }

    // If deal has orderItems, use them
    if (deal?.orderItems && deal.orderItems.length > 0) {
      return deal.orderItems.map(item => ({
        productId: item.productId,
        name: item.name,
        sku: item.sku || '',
        quantity: item.quantity,
        unit: item.unit || item.product?.unit || 'pcs',
        unitPrice: Number(item.price),
        discountPercent: 0,
      }));
    }

    // Default empty item
    return [{
      name: '',
      sku: '',
      quantity: 1,
      unit: 'pcs',
      unitPrice: 0,
      discountPercent: 0,
    }];
  };

  const [items, setItems] = useState<CreateQuoteItemDto[]>(getInitialItems());

  useEffect(() => {
    fetchProducts();
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (!shouldSelectClient) return;

    const timeout = window.setTimeout(() => {
      fetchClients({ page: 1, search: clientSearch, append: false });
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [shouldSelectClient, clientSearch]);

  useEffect(() => {
    const quantitiesByProductAndUnit = new Map<string, { productId: number; requiredQuantity: number; unit?: string }>();

    items.forEach((item) => {
      if (!item.productId) return;
      const key = getStockKey(item.productId, item.unit);
      const existing = quantitiesByProductAndUnit.get(key);
      quantitiesByProductAndUnit.set(key, {
        productId: item.productId,
        requiredQuantity: (existing?.requiredQuantity || 0) + (Number(item.quantity) || 0),
        unit: item.unit,
      });
    });

    const availabilityItems = Array.from(quantitiesByProductAndUnit.values())
      .filter((item) => item.requiredQuantity > 0);

    if (availabilityItems.length === 0) {
      setStockDetails({});
      setCheckingStock(false);
      return;
    }

    const timeout = window.setTimeout(async () => {
      try {
        setCheckingStock(true);
        const result = await stockAlertsAPI.checkAvailability(availabilityItems);
        setStockDetails(
          result.details.reduce((acc, detail) => {
            acc[getStockKey(detail.productId, detail.requestedUnit)] = detail;
            return acc;
          }, {} as typeof stockDetails),
        );
      } catch (err) {
        console.error('Failed to check inventory availability:', err);
      } finally {
        setCheckingStock(false);
      }
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [items]);

  const fetchProducts = async () => {
    try {
      const data = await catalogueAPI.getProducts({ isActive: true });
      setProducts(data);
    } catch (err: any) {
      console.error('Failed to fetch products:', err);
    }
  };

  const fetchCompanies = async () => {
    try {
      const data = await companiesAPI.getAll();
      const selectableCompanies = data.filter((company) => company.isActive || company.id === quote?.companyId);
      const activeCompanies = selectableCompanies.filter((company) => company.isActive);
      setCompanies(selectableCompanies);

      if (quote?.companyId) {
        setSelectedCompanyId(String(quote.companyId));
      } else if (activeCompanies.length === 1) {
        setSelectedCompanyId(String(activeCompanies[0].id));
      }
    } catch (err: any) {
      console.error('Failed to fetch companies:', err);
      setError(err.response?.data?.message || 'Failed to load companies');
    }
  };

  const fetchClients = async (options?: { page?: number; search?: string; append?: boolean }) => {
    const page = options?.page || 1;
    const search = options?.search?.trim() || undefined;
    const requestId = ++clientRequestId.current;

    try {
      setLoadingClients(true);
      const data = await clientsAPI.getAll({
        page,
        limit: CLIENT_SEARCH_PAGE_SIZE,
        search,
      });

      if (requestId !== clientRequestId.current) return;

      const nextClients = Array.isArray(data) ? data : (data as any).data || [];
      const pagination = Array.isArray(data) ? undefined : (data as any).pagination;

      setAvailableClients((currentClients) => (
        options?.append ? mergeClientResults(currentClients, nextClients) : nextClients
      ));
      setClientPagination({
        page: Number(pagination?.page || page),
        limit: Number(pagination?.limit || CLIENT_SEARCH_PAGE_SIZE),
        total: Number(pagination?.total || nextClients.length),
        pages: Number(pagination?.pages || pagination?.totalPages || 1),
      });
    } catch (err: any) {
      if (requestId !== clientRequestId.current) return;
      console.error('Failed to fetch clients:', err);
      setError(err.response?.data?.message || 'Failed to load clients');
    } finally {
      if (requestId === clientRequestId.current) {
        setLoadingClients(false);
      }
    }
  };

  const handleClientSearchChange = (value: string) => {
    setClientSearch(value);
    if (selectedClientId) {
      setSelectedClientId('');
      setSelectedClient(undefined);
    }
  };

  const handleClientSelect = (clientId: string) => {
    const selected = availableClients.find((availableClient) => availableClient.id === Number(clientId));
    if (!selected) {
      setSelectedClientId('');
      setSelectedClient(undefined);
      return;
    }

    setSelectedClientId(clientId);
    setSelectedClient(selected);
    setClientSearch(selected.name);

    setFormData((current) => {
      const genericIntroduction = 'Thank you for your interest. Please find our quote below.';
      return {
        ...current,
        title: !current.title || current.title === 'Quote for Client'
          ? `Quote for ${selected.name}`
          : current.title,
        introduction: !current.introduction || current.introduction === genericIntroduction
          ? `Dear ${selected.name},\n\nThank you for your interest. Please find our quote below.`
          : current.introduction,
      };
    });
  };

  const handleLoadMoreClients = () => {
    if (loadingClients || !hasMoreClients) return;

    fetchClients({
      page: clientPagination.page + 1,
      search: clientSearch,
      append: true,
    });
  };

  const getPricingUnit = (product: Product, unit?: string | null) => {
    return unit?.trim() || product.unit || 'pcs';
  };

  const getPricingUnits = (product?: Product, currentUnit?: string) => {
    const units = new Set<string>();
    if (currentUnit?.trim()) units.add(currentUnit.trim());
    if (product?.unit?.trim()) units.add(product.unit.trim());
    if (product) {
      product.costRanges?.forEach((range) => {
        const unit = getPricingUnit(product, range.unit);
        if (unit) units.add(unit);
      });
    }
    return Array.from(units);
  };

  const getMatchingCostRange = (product: Product, quantity: number, unit?: string | null) => {
    const normalizedUnit = normalizeUnit(unit || product.unit);
    return product.costRanges?.find((range) => {
      const min = Number(range.minQuantity);
      const max = range.maxQuantity === null || range.maxQuantity === undefined
        ? Number.POSITIVE_INFINITY
        : Number(range.maxQuantity);
      const rangeUnit = normalizeUnit(getPricingUnit(product, range.unit));
      return quantity >= min && quantity <= max && (!normalizedUnit || rangeUnit === normalizedUnit);
    });
  };

  const getDefaultUnitPrice = (product: Product, quantity: number, unit?: string | null) => {
    const matchingRange = getMatchingCostRange(product, quantity, unit)
      || product.costRanges?.find((range) => {
        const min = Number(range.minQuantity);
        const max = range.maxQuantity === null || range.maxQuantity === undefined
          ? Number.POSITIVE_INFINITY
          : Number(range.maxQuantity);
        return quantity >= min && quantity <= max;
      });

    return matchingRange ? Number(matchingRange.pricePerUnit) : 0;
  };

  const handleProductSelect = (index: number, productId: string) => {
    const selectedProduct = products.find((product) => product.id === Number(productId));
    if (!selectedProduct) {
      updateItem(index, 'productId', undefined);
      return;
    }

    const quantity = items[index]?.quantity || 1;
    const matchingRange = getMatchingCostRange(selectedProduct, quantity, selectedProduct.unit)
      || selectedProduct.costRanges?.[0];
    const selectedUnit = getPricingUnit(selectedProduct, matchingRange?.unit || selectedProduct.unit || items[index]?.unit);
    const defaultUnitPrice = getDefaultUnitPrice(selectedProduct, quantity, selectedUnit);
    const updated = [...items];
    updated[index] = {
      ...updated[index],
      productId: selectedProduct.id,
      name: selectedProduct.name,
      description: selectedProduct.description,
      sku: selectedProduct.sku,
      unit: selectedUnit,
      unitPrice: defaultUnitPrice,
    };
    setItems(updated);
  };

  const handleUnitChange = (index: number, unit: string) => {
    const updated = [...items];
    const currentItem = updated[index];
    const selectedProduct = currentItem.productId
      ? products.find((product) => product.id === currentItem.productId)
      : undefined;
    updated[index] = {
      ...currentItem,
      unit,
      unitPrice: selectedProduct
        ? getDefaultUnitPrice(selectedProduct, currentItem.quantity || 1, unit) || currentItem.unitPrice
        : currentItem.unitPrice,
    };
    setItems(updated);
  };

  const addItem = () => {
    setItems([...items, {
      name: '',
      quantity: 1,
      unit: 'pcs',
      unitPrice: 0,
      discountPercent: 0,
    }]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: keyof CreateQuoteItemDto, value: any) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const calculateItemTotal = (item: CreateQuoteItemDto) => {
    const itemTotal = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);
    const discount = itemTotal * (item.discountPercent || 0) / 100;
    return itemTotal - discount;
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => {
      return sum + calculateItemTotal(item);
    }, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = subtotal * formData.discountPercent / 100;
    const afterDiscount = subtotal - discount;
    const tax = afterDiscount * formData.taxPercent / 100;
    return afterDiscount + tax;
  };

  const getStockTone = (status: InventoryAvailabilityStatus) => {
    switch (status) {
      case 'AVAILABLE':
        return 'border-success-200 bg-success-50 text-success-700';
      case 'PARTIAL':
        return 'border-warning-200 bg-warning-50 text-warning-700';
      case 'OUT_OF_STOCK':
        return 'border-error-200 bg-error-50 text-error-700';
      case 'NOT_TRACKED':
      default:
        return 'border-neutral-200 bg-neutral-50 text-neutral-600';
    }
  };

  const getStockLabel = (status: InventoryAvailabilityStatus) => {
    switch (status) {
      case 'AVAILABLE':
        return 'Available';
      case 'PARTIAL':
        return 'Partial stock';
      case 'OUT_OF_STOCK':
        return 'Out of stock';
      case 'NOT_TRACKED':
      default:
        return 'Not inventory-tracked';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCompanyId) {
      setError('Please select a company');
      return;
    }
    
    if (items.length === 0) {
      setError('Please add at least one item');
      return;
    }

    if (items.some(item => !item.name || !item.unit?.trim() || item.quantity <= 0 || item.unitPrice <= 0)) {
      setError('Please fill in all item details with valid values');
      return;
    }

    if (!quoteClient?.id && !deal?.id) {
      setError('Please select a client before creating a quote');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const quoteData: CreateQuoteDto = {
        companyId: Number(selectedCompanyId),
        dealId: quote?.dealId || deal?.id,
        clientId: quoteClient?.id,
        ...formData,
        // Convert date to ISO-8601 DateTime format
        validUntil: formData.validUntil ? new Date(formData.validUntil).toISOString() : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        items: items.map((item, index) => ({
          ...item,
          unit: item.unit?.trim() || 'piece',
          order: index + 1,
        })),
      };

      if (quote) {
        await quotesAPI.update(quote.id, quoteData);
      } else {
        await quotesAPI.create(quoteData);
      }
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${quote ? 'update' : 'create'} quote`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={quote ? 'Edit Quote' : 'Create Quote'}
      subtitle={quote
        ? `${quote.quoteNumber} - ${quoteClient?.name || 'Unknown client'}`
        : deal
        ? `Deal: ${deal.description} - Client: ${quoteClient?.name || 'Unknown client'}`
        : shouldSelectClient
          ? 'Select a client and add products'
          : `Client: ${quoteClient?.name || 'Unknown client'}`}
      size="xl"
    >
      {error && (
        <div className="mb-4 bg-error-50 border border-error-200 text-error-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Quote Details */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label htmlFor="quote-company" className="block text-sm font-medium text-neutral-700">
              Company <span className="text-error-600">*</span>
            </label>
            <select
              id="quote-company"
              value={selectedCompanyId}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              required
            >
              <option value="">
                {companies.some((company) => company.isActive)
                  ? 'Select company'
                  : 'No active companies available'}
              </option>
              {companies.map((company) => (
                <option key={company.id} value={company.id} disabled={!company.isActive && company.id !== quote?.companyId}>
                  {company.name}{company.isActive ? '' : ' (Inactive)'}
                </option>
              ))}
            </select>
          </div>
          {shouldSelectClient && (
            <div className="md:col-span-2">
              <label htmlFor="quote-client-search" className="block text-sm font-medium text-neutral-700">
                Client <span className="text-error-600">*</span>
              </label>
              <input
                id="quote-client-search"
                type="search"
                value={clientSearch}
                onChange={(e) => handleClientSearchChange(e.target.value)}
                className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="Name, email, or phone"
                autoComplete="off"
              />
              <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-sm">
                {availableClients.length > 0 ? (
                  <div className="divide-y divide-neutral-100">
                    {availableClients.map((availableClient) => {
                      const isSelected = selectedClientId === String(availableClient.id);
                      return (
                        <button
                          key={availableClient.id}
                          type="button"
                          onClick={() => handleClientSelect(String(availableClient.id))}
                          className={`flex w-full flex-col gap-0.5 px-3 py-2 text-left text-sm transition-colors ${
                            isSelected
                              ? 'bg-primary-50 text-primary-700'
                              : 'text-neutral-700 hover:bg-neutral-50'
                          }`}
                        >
                          <span className="font-medium">{availableClient.name}</span>
                          {availableClient.email && (
                            <span className="text-xs text-neutral-500">{availableClient.email}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="px-3 py-4 text-sm text-neutral-500">
                    {loadingClients ? 'Loading clients...' : 'No clients found'}
                  </div>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-500">
                <span>
                  {clientPagination.total > 0
                    ? `Showing ${availableClients.length} of ${clientPagination.total} clients`
                    : 'No clients to show'}
                </span>
                {hasMoreClients && (
                  <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    onClick={handleLoadMoreClients}
                    isLoading={loadingClients}
                  >
                    Load more
                  </Button>
                )}
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-neutral-700">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="Quote for..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700">Valid Until</label>
            <input
              type="date"
              value={formData.validUntil}
              onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700">Introduction</label>
          <textarea
            value={formData.introduction}
            onChange={(e) => setFormData({ ...formData, introduction: e.target.value })}
            rows={2}
            className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            placeholder="Thank you for your interest..."
          />
        </div>

        {/* Items */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-neutral-700">Quote Items</label>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={addItem}
              leftIcon={<PlusIcon className="h-4 w-4" />}
            >
              Add Item
            </Button>
          </div>

          <div className="space-y-3">
            {items.map((item, index) => (
              <Card key={index} variant="bordered">
                <CardBody className="p-4">
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
                      <div className="min-w-0 lg:col-span-5">
                        <label className="block text-xs font-medium text-neutral-700">Saved Product</label>
                        <select
                          value={item.productId || ''}
                          onChange={(e) => handleProductSelect(index, e.target.value)}
                          className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                        >
                          <option value="">Manual item</option>
                          {products.map((product) => (
                            <option key={product.id} value={product.id}>
                              {product.sku} - {product.name}{product.isInventoryLinked === false ? ' (not inventory-linked)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="min-w-0 lg:col-span-5">
                        <label className="block text-xs font-medium text-neutral-700">Product Name</label>
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => updateItem(index, 'name', e.target.value)}
                          className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                          required
                        />
                      </div>
                      <div className="min-w-0 lg:col-span-2">
                        <label className="block text-xs font-medium text-neutral-700">SKU</label>
                        <input
                          type="text"
                          value={item.sku || ''}
                          onChange={(e) => updateItem(index, 'sku', e.target.value)}
                          className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 md:grid-cols-12">
                      <div className="min-w-0 md:col-span-2">
                        <label className="block text-xs font-medium text-neutral-700">Quantity</label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value))}
                          className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      <div className="min-w-0 md:col-span-2">
                        <label className="block text-xs font-medium text-neutral-700">Unit</label>
                        <input
                          type="text"
                          list={`quote-unit-options-${index}`}
                          value={item.unit || ''}
                          onChange={(e) => handleUnitChange(index, e.target.value)}
                          className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                          placeholder="kg, carton, pcs"
                          required
                        />
                        <datalist id={`quote-unit-options-${index}`}>
                          {getPricingUnits(
                            item.productId ? products.find((product) => product.id === item.productId) : undefined,
                            item.unit,
                          ).map((unit) => (
                            <option key={unit} value={unit} />
                          ))}
                        </datalist>
                      </div>
                      <div className="min-w-0 md:col-span-2">
                        <label className="block text-xs font-medium text-neutral-700">Unit Price</label>
                        <input
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value))}
                          className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      <div className="col-span-2 min-w-0 md:col-span-4">
                        <label className="block text-xs font-medium text-neutral-700">Line Total</label>
                        <div className="mt-1 flex min-h-[38px] items-center rounded-lg border border-neutral-200 bg-neutral-50 px-3 text-sm font-semibold text-neutral-900">
                          {formData.currency} {calculateItemTotal(item).toFixed(2)}
                        </div>
                      </div>
                      <div className="col-span-2 flex items-end justify-end md:col-span-2">
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="w-full md:w-auto"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {item.productId && stockDetails[getStockKey(item.productId, item.unit)] && (
                      <div className={`rounded-lg border px-3 py-2 text-sm ${getStockTone(stockDetails[getStockKey(item.productId, item.unit)].status)}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-medium">
                            {stockDetails[getStockKey(item.productId, item.unit)].unitMismatch
                              ? `Inventory not checked for ${stockDetails[getStockKey(item.productId, item.unit)].requestedUnit || item.unit}`
                              : getStockLabel(stockDetails[getStockKey(item.productId, item.unit)].status)}
                          </span>
                          {stockDetails[getStockKey(item.productId, item.unit)].unitMismatch ? (
                            <span>
                              Inventory unit: {stockDetails[getStockKey(item.productId, item.unit)].inventoryUnit || 'not set'}
                            </span>
                          ) : stockDetails[getStockKey(item.productId, item.unit)].isInventoryLinked && (
                            <span>
                              Available {stockDetails[getStockKey(item.productId, item.unit)].availableQuantity} / Required {stockDetails[getStockKey(item.productId, item.unit)].requiredQuantity}
                            </span>
                          )}
                        </div>
                        {stockDetails[getStockKey(item.productId, item.unit)].inventoryItems.length > 0 && (
                          <div className="mt-1 text-xs opacity-90">
                            Lots: {stockDetails[getStockKey(item.productId, item.unit)].inventoryItems
                              .map((inventoryItem) => inventoryItem.batchNumber)
                              .filter(Boolean)
                              .slice(0, 4)
                              .join(', ') || '-'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        </div>

        {/* Pricing */}
        <Card variant="bordered" className="bg-primary-50">
          <CardBody className="p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="block text-sm font-medium text-neutral-700">Discount (%)</label>
                <input
                  type="number"
                  value={formData.discountPercent}
                  onChange={(e) => setFormData({ ...formData, discountPercent: parseFloat(e.target.value) || 0 })}
                  className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700">Tax (%)</label>
                <input
                  type="number"
                  value={formData.taxPercent}
                  onChange={(e) => setFormData({ ...formData, taxPercent: parseFloat(e.target.value) || 0 })}
                  className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700">Total</label>
                <div className="mt-1 block w-full px-3 py-2 bg-white border border-neutral-300 rounded-lg text-lg font-bold text-primary-600">
                  {formData.currency} {calculateTotal().toFixed(2)}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Terms & Notes */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-neutral-700">Terms & Conditions</label>
            <textarea
              value={formData.terms}
              onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
              rows={3}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="Payment terms, delivery conditions..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700">Internal Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="Internal notes (not visible to client)..."
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse gap-3 border-t border-neutral-200 pt-4 sm:flex-row sm:items-center sm:justify-end">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={loading}
            className="w-full sm:w-auto"
          >
            {checkingStock ? 'Checking Stock...' : quote ? 'Save Quote' : 'Create Quote'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default QuoteModal;
